import { useState } from "react";
import { Link, useActionData, useNavigation, useParams, useRouteLoaderData, useSubmit, type ActionFunctionArgs } from "react-router";
import type { DecisionImport, DecisionImportResult } from "@edm/contracts";
import { FieldSkeleton, Loading } from "../../components/skeletons.jsx";
import { translate, useI18n } from "../../i18n/index.js";
import { isApiError } from "../../lib/api.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { ArrowLeftIcon, Badge, Button, Callout, Card, FieldGroup, FileField, FormError, PageHeader, Select, Skeleton, StatusBadge, Table, TextField } from "../../ui/index.js";
import type { workspaceLayoutLoader } from "./layout.jsx";

// Markdown import (ENG-12): preview is a dry run of exactly what an import
// would do; conflicts between files and memory are shown, never resolved
// silently. Files are read in the browser and sent as JSON.

type ActionResult = { result?: DecisionImportResult; error?: string };
type Submission = { intent: "preview" | "apply"; files: { path: string; content: string }[]; onConflict: DecisionImport["onConflict"]; repository: string };

export async function decisionImportAction({ context, request }: ActionFunctionArgs): Promise<ActionResult> {
  const ws = context.get(workspaceContext);
  const input = (await request.json()) as Submission;
  if (input.files.length === 0) return { error: translate("import.noFiles") };
  try {
    const result = await context
      .get(apiContext)
      .workspace(ws.id)
      .importDecisions({
        files: input.files as DecisionImport["files"],
        dryRun: input.intent !== "apply",
        ...(input.onConflict ? { onConflict: input.onConflict } : {}),
        ...(input.repository.trim() ? { repository: input.repository.trim() } : {}),
      });
    return { result };
  } catch (err) {
    if (isApiError(err, "validation_failed") || isApiError(err, "forbidden")) return { error: err.message };
    throw err;
  }
}

function readText(file: File): Promise<string> {
  if (typeof file.text === "function") return file.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function ImportHeader() {
  const { workspaceSlug } = useParams();
  const { t } = useI18n();
  return (
    <PageHeader
      eyebrow={
        <Link to={`/w/${workspaceSlug}/decisions`} className="dt-inline dt-gap-1 dt-align-center" style={{ color: "inherit" }}>
          <ArrowLeftIcon /> {t("decisions.title")}
        </Link>
      }
      title={t("import.title")}
      description={t("import.description")}
    />
  );
}

export function DecisionImportSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <ImportHeader />
      <Card>
        <Loading>
          <div className="dt-form" style={{ maxWidth: 640 }}>
            <FieldSkeleton label={t("import.files")} />
            <FieldSkeleton label={t("import.repository")} />
            <div className="dt-field">
              <span className="dt-field__label">{t("import.onConflict")}</span>
              <Skeleton shape="select" width={200} />
            </div>
            <div className="dt-form__actions">
              <Skeleton shape="button" width={88} />
              <Skeleton shape="button" width={88} />
            </div>
          </div>
        </Loading>
      </Card>
    </>
  );
}

export function DecisionImportRoute() {
  const data = useActionData() as ActionResult | undefined;
  const busy = useNavigation().state === "submitting";
  const submit = useSubmit();
  const { workspace } = useRouteLoaderData("workspace") as ReturnType<typeof workspaceLayoutLoader>;
  const { t, tOr, tNode, tPlural } = useI18n();
  const [files, setFiles] = useState<{ path: string; content: string }[]>([]);
  const [onConflict, setOnConflict] = useState<NonNullable<DecisionImport["onConflict"]>>("fail");
  const [repository, setRepository] = useState("");
  const conflictOptions = [
    { value: "fail", label: t("import.onConflict.fail") },
    { value: "prefer_cloud", label: t("import.onConflict.preferCloud") },
    { value: "prefer_file", label: t("import.onConflict.preferFile") },
  ] satisfies { value: NonNullable<DecisionImport["onConflict"]>; label: string }[];
  const base = `/w/${workspace.slug}`;
  const result = data?.result;

  const send = (intent: Submission["intent"]) => submit({ intent, files, onConflict, repository } satisfies Submission, { method: "post", encType: "application/json" });

  return (
    <>
      <ImportHeader />
      <Card>
        <div className="dt-form" style={{ maxWidth: 640 }}>
          <FileField
            name="files"
            label={t("import.files")}
            description={t("import.filesHint")}
            accept=".md,.markdown,text/markdown"
            multiple
            selected={files.map((f) => f.path)}
            status={files.length === 0 ? t("import.filesEmpty") : tPlural("import.filesCount", files.length)}
            actionLabel={t("import.filesAction")}
            clearLabel={t("import.filesClear")}
            onSelect={async (picked) => {
              setFiles(await Promise.all(picked.map(async (f) => ({ path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name, content: await readText(f) }))));
            }}
            onClear={() => setFiles([])}
          />
          <TextField
            name="repository"
            label={t("import.repository")}
            value={repository}
            onChange={(e) => setRepository(e.currentTarget.value)}
            placeholder={t("import.repositoryPlaceholder")}
            description={t("import.repositoryHint")}
          />
          <FieldGroup name="onConflict" label={t("import.onConflict")}>
            <Select name="onConflict" items={conflictOptions} value={onConflict} onValueChange={setOnConflict} aria-label={t("import.onConflict")} />
          </FieldGroup>
          <FormError message={data?.error} />
          <div className="dt-form__actions">
            <Button variant="secondary" loading={busy} disabled={files.length === 0} onClick={() => send("preview")}>
              {t("import.preview")}
            </Button>
            <Button variant="primary" loading={busy} disabled={files.length === 0} onClick={() => send("apply")}>
              {t("import.apply")}
            </Button>
          </div>
        </div>
      </Card>
      {result && (
        <Card title={result.dryRun ? t("import.previewTitle") : t("import.resultTitle")} padded={false}>
          {result.dryRun && (
            <div style={{ padding: "var(--dt-space-4)" }}>
              <Callout tone="info">{t("import.previewNote")}</Callout>
            </div>
          )}
          <Table>
            <thead>
              <tr>
                <th>{t("import.th.file")}</th>
                <th className="dt-nowrap">{t("import.th.decision")}</th>
                <th>{t("import.th.action")}</th>
                <th>{t("import.th.details")}</th>
              </tr>
            </thead>
            <tbody>
              {result.items.map((it) => (
                <tr key={it.path} data-testid="import-item">
                  <td className="dt-mono">{it.path}</td>
                  <td className="dt-nowrap">
                    {it.decisionId && !result.dryRun ? <Link to={`${base}/decisions/${it.decisionId}`}>{it.decisionId}</Link> : (it.decisionId ?? t("common.none"))}
                  </td>
                  <td>
                    <StatusBadge status={it.action === "created" || it.action === "updated" ? "accepted" : it.action === "unchanged" ? "draft" : "rejected"} />{" "}
                    <Badge tone="neutral">{tOr(`importAction.${it.action}`, it.action)}</Badge>
                  </td>
                  <td>
                    {it.error && <div className="dt-muted">{it.error}</div>}
                    {it.conflicts?.map((c) => (
                      <div key={c.field} className="dt-muted">
                        {tNode("import.conflictLine", { field: <strong>{c.field}</strong>, file: c.file, cloud: c.cloud })}
                      </div>
                    ))}
                    {it.warnings?.map((w) => (
                      <div key={w} className="dt-subtle">
                        {w}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}
